import { useState, useCallback, useRef } from 'react';
import {
  useUsersService,
  ProfileFilterRequest,
  ProfileFilterResponse,
  DomainType
} from '@app/api/users';
import { HooksStatus } from './types';

export interface ProfileFilterState {
  data?: ProfileFilterResponse;
  status: HooksStatus;
  error?: string;
}

export interface UseProfileFilterResult {
  profileFilter: ProfileFilterState;
  filterProfiles: (request: ProfileFilterRequest) => Promise<void>;
  clearFilter: () => void;
  setAsValidWithoutApiCall: (domainType: DomainType, allowedProfiles?: string[]) => void;
  isLoading: boolean;
  hasError: boolean;
  isValid: boolean;
  allowedProfiles: string[];
  domainType?: DomainType;
}

export const useProfileFilter = (): UseProfileFilterResult => {
  const usersService = useUsersService();
  const usersServiceRef = useRef(usersService);
  usersServiceRef.current = usersService;

  const [profileFilter, setProfileFilter] = useState<ProfileFilterState>({
    status: 'idle'
  });

  const filterProfiles = useCallback(async (request: ProfileFilterRequest) => {
    try {
      setProfileFilter(prev => ({ ...prev, status: 'loading', error: undefined }));

      const response = await usersServiceRef.current.getAllowedProfiles(request);

      setProfileFilter({
        data: response,
        status: 'succeeded',
        error: undefined
      });
    } catch (error) {
      setProfileFilter({
        data: undefined,
        status: 'error',
        error: error instanceof Error ? error.message : 'An error occurred while filtering profiles'
      });
    }
  }, []);

  const clearFilter = useCallback(() => {
    setProfileFilter({
      status: 'idle',
      data: undefined,
      error: undefined
    });
  }, []);

  const setAsValidWithoutApiCall = useCallback((domainType: DomainType, allowedProfiles: string[] = []) => {
    setProfileFilter({
      status: 'succeeded',
      data: {
        email: '',
        isValid: true,
        allowedProfiles: allowedProfiles,
        domainType: domainType
      },
      error: undefined
    });
  }, []);

  const isLoading = profileFilter.status === 'loading';
  const hasError = profileFilter.status === 'error';
  const isValid = profileFilter.data?.isValid ?? false;
  const allowedProfiles = profileFilter.data?.allowedProfiles ?? []; // Now returns string[] with role names
  const domainType = profileFilter.data?.domainType;

  return {
    profileFilter,
    filterProfiles,
    clearFilter,
    setAsValidWithoutApiCall,
    isLoading,
    hasError,
    isValid,
    allowedProfiles,
    domainType
  };
};